import dayjs from 'dayjs'

import { dateUtils } from '../..'
import type { en } from '../translations/en'
import type { SourceReaderFunction } from '../types/translationTypes'
import type { Optional } from '../types/types'
import { isEmpty, notEmpty } from './typeUtils'

export const MAX_DATE_STRING = '9999-12-31'
export const MAX_DATE = new Date(MAX_DATE_STRING)
export const SERVER_DATE_FORMAT = 'YYYY-MM-DD'
export const SERVER_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm'
const RANGE_DASH = '\u2013' // we use en dash instead of hyphen for ranges
const THIN_SPACE = '\u202F' // narrow no-break space, for a bit of separation

export const isValidDate = (d: Optional<Date | string>): d is Date | string =>
  notEmpty(d) && dayjs(d).isValid()

// dayjs will treat 000x values as 190x: https://github.com/iamkun/dayjs/issues/1237
// Seems like there is a fix in the issue. But the issue is also quite old.
// For now be weary of using dayjs for date inputs.
export const dateAsServerStr = <D extends Optional<Date | string>>(
  d: D,
): D extends string | Date ? string : string | undefined => {
  if (!d) {
    return undefined as D extends string | Date ? string : string | undefined
  }

  return dayjs(d).format(SERVER_DATE_FORMAT)
}

export const dateAsDateTimeStr = <D extends Optional<Date | string>>(
  d: D,
): D extends string | Date ? string : string | undefined => {
  if (!d) {
    return undefined as D extends string | Date ? string : string | undefined
  }

  return dayjs(d).format(SERVER_DATE_TIME_FORMAT)
}

/** Treat a naive date as UTC+00:00, and return it in the local time. */
export function dateUtcAsLocal(d: string): dayjs.Dayjs
export function dateUtcAsLocal(d: Date): dayjs.Dayjs
export function dateUtcAsLocal(d: Date | string): dayjs.Dayjs

export function dateUtcAsLocal(d: Date | string): dayjs.Dayjs {
  if (typeof d === 'string' && d !== '') {
    // Currently the backend never sends back timezone information.
    // See APGDEFECTS-1383?focusedCommentId=100099
    // But if it ever does, we must be careful not to create an invalid date.
    return d.includes('T') && !/(?:Z|[±+-][\d:]+)$/.test(d)
      ? dayjs.utc(d).local()
      : dayjs(d)
  }

  // Date objects could be created incorrectly without an offset.
  // But we cannot know, because the objects do not store the original string that was parsed.
  // The only safe thing to do is to assume the caller has done their homework and leave it unchanged.
  return dayjs(d)
}

export const dateAsStr = (d: Optional<Date | string>) => {
  if (!d) {
    return '-'
  }

  return dateUtcAsLocal(d).format('L')
}

export const dateRangeAsStr = (
  startDate: Optional<Date | string>,
  endDate: Optional<Date | string>,
) => {
  const start = startDate ? `${dateAsStr(startDate)}${THIN_SPACE}` : ''
  const end = endDate ? `${THIN_SPACE}${dateAsStr(endDate)}` : ''
  return `${start}${RANGE_DASH}${end}`
}

export const dateWithTimeAsStr = (d: Optional<Date | string>) => {
  if (!d) {
    return '-'
  }

  return dateUtcAsLocal(d).format('lll')
}

export const dateWithPreciseTimeAsStr = (d: Optional<Date | string>) => {
  if (!d) {
    return '-'
  }

  return dateUtcAsLocal(d).format('L LTS.SSS')
}

export const dateFormatter = (date: Optional<string>) => {
  if (!date) {
    return ''
  }

  return dayjs(date).format('L, LTS')
}

export const getDateValues = (date: Date) => {
  const day = date.getDay()
  const month = (date.getMonth() + 1) as
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 12
  const year = date.getFullYear()

  return { day, month, year }
}

export const getAge = ({
  dateString,
  diffDate,
  translatedMonth,
  translatedYear,
}: {
  dateString: Optional<string>
  diffDate: Optional<string>
  translatedMonth: string
  translatedYear: string
}) => {
  if (!dateString || !diffDate) {
    return '-'
  }

  const year = getYearFromDate(dateString, diffDate)
  const month = getMonthFromDate(dateString, diffDate)
  const monthString = month ? `, ${month} ${translatedMonth}` : ''

  return `${year} ${translatedYear}${monthString}`
}

export const getAgeInYears = (
  startDate: Optional<string>,
  endDate: Optional<string>,
) => {
  if (!startDate || !endDate) {
    return '-'
  }

  return getYearFromDate(startDate, endDate)
}

export const getMonthFromDate = (
  dateString: Optional<string>,
  diffDate: Optional<string>,
) => {
  if (!dateString || !diffDate) {
    return '-'
  }

  const birthDate = dayjs(diffDate)
  const years = birthDate.diff(dateString, 'year') * 12

  return birthDate.diff(dateString, 'month') - years
}

export const getMonthsFromDate = (
  dateString: Optional<string>,
  diffDate: Optional<string>,
) => {
  if (!dateString || !diffDate) {
    return '-'
  }

  const birthDate = dayjs(diffDate)

  return birthDate.diff(dateString, 'month')
}

export const getYearFromDate = (
  dateString: Optional<string>,
  diffDate: Optional<string>,
) => {
  if (!dateString || !diffDate) {
    return '-'
  }

  const birthDate = dayjs(diffDate)

  return birthDate.diff(dateString, 'year').toString()
}

export const formatMonths = ({
  months,
  translatedMonth,
  translatedYear,
}: {
  months: number
  translatedMonth: Optional<string>
  translatedYear: string
}) => {
  const years = Math.floor(months / 12)
  const fractionMonths = months % 12
  const formattedMonth = notEmpty(translatedMonth)
    ? `, ${fractionMonths} ${translatedMonth}`
    : ''

  return `${years} ${translatedYear}${formattedMonth}`
}

export const formatDateAndAge = ({
  startDate,
  endDate,
  displayDate = 'startDate',
  translatedYear,
  translatedMonth,
}: {
  startDate: Optional<string>
  endDate: Optional<string>
  displayDate?: 'startDate' | 'endDate'
  translatedYear: string
  translatedMonth?: Optional<string>
}) => {
  if (!startDate || !endDate) {
    return '-'
  }

  const formattedAge = formatAge({
    startDate,
    endDate,
    translatedYear,
    translatedMonth,
  })

  const date = dateAsStr(displayDate === 'startDate' ? startDate : endDate)

  return `${date} (${formattedAge})`
}

export const formatAge = ({
  startDate,
  endDate,
  translatedYear,
  translatedMonth,
}: {
  startDate: Optional<string>
  endDate: Optional<string>
  translatedYear: string
  translatedMonth?: Optional<string>
}) => {
  if (!startDate || !endDate) {
    return '-'
  }

  const allMonths = Number(getMonthsFromDate(startDate, endDate))

  return formatMonths({
    months: allMonths,
    translatedMonth,
    translatedYear,
  })
}

/**
 * Compare two server dates.
 *
 * Nullable dates are considered "smaller" and will come first.
 *
 * The result will cause a sort to be in ascending order.
 */
export const compare = (
  leftDate: Optional<string>,
  rightDate: Optional<string>,
) => {
  if (notEmpty(leftDate)) {
    if (notEmpty(rightDate)) {
      return leftDate.localeCompare(rightDate)
    } else {
      return 1
    }
  } else if (notEmpty(rightDate)) {
    return -1
  } else {
    return 0
  }
}

export type RemunerationPeriodDuration =
  | 'halfYearly'
  | 'monthly'
  | 'quarterly'
  | 'single'
  | 'yearly'

type PeriodData<T> = {
  frequency: RemunerationPeriodDuration
  startDate: string
  endDate?: Optional<string>
  value: T
}

type SpreadMonthlyProps<T> = {
  periods: PeriodData<T>[]
  startDate: string
  endDate: string
}

type MonthlySpread<T> = {
  year: number
  month: number
  values: T[]
}

/**
 * Given a list of periods, spread them as if they were monthly.
 *
 * Start date is inclusive, but end date is exclusive.
 *
 * The first day of each month is checked.
 */
export const spreadMonthlyPeriods = <T>({
  periods,
  startDate,
  endDate,
}: SpreadMonthlyProps<T>): MonthlySpread<T>[] => {
  // key is `${year}/${month}`, value is corresponding periods
  const valuesPerMonth: Record<string, T[]> = {}

  const start = dayjs(startDate)
  const end = dayjs(endDate)
  const startMonth = start.startOf('month')

  const stepForPeriod = (frequency: RemunerationPeriodDuration): number => {
    switch (frequency) {
      case 'monthly':
        return 1
      case 'quarterly':
        return 3
      case 'halfYearly':
        return 6
      case 'yearly':
        return 12
      case 'single':
        // Step large enough that would only occur once
        return end.diff(start, 'month') + 1
      default:
        throw new Error(`unsupported frequency: ${frequency}`)
    }
  }

  const endForPeriod = (periodEnd: Optional<string>): string => {
    if (notEmpty(periodEnd)) {
      return periodEnd < endDate ? periodEnd : endDate
    } else {
      return endDate
    }
  }

  for (const period of periods) {
    const step = stepForPeriod(period.frequency)
    const periodEnd = dayjs(endForPeriod(period.endDate))

    let cursor = dayjs(period.startDate)

    while (cursor.startOf('month') < periodEnd) {
      const cursorMonth = cursor.startOf('month')

      if (cursorMonth >= startMonth && cursorMonth < end) {
        const key = `${cursor.year()}/${cursor.month()}`

        if (valuesPerMonth[key] === undefined) {
          valuesPerMonth[key] = []
        }
        valuesPerMonth[key].push(period.value)
      }
      cursor = cursor.add(step, 'month')
    }
  }

  const result: MonthlySpread<T>[] = []

  let cursor = start

  while (cursor < end) {
    const key = `${cursor.year()}/${cursor.month()}`
    result.push({
      year: cursor.year(),
      month: cursor.month(),
      values: valuesPerMonth[key] ?? [],
    })
    cursor = cursor.add(1, 'month')
  }

  return result
}

export const getMaxDate = (
  dates: Optional<string>[],
  returnFormat:
    | typeof SERVER_DATE_FORMAT
    | typeof SERVER_DATE_TIME_FORMAT = SERVER_DATE_FORMAT,
): Optional<string> => {
  const validDates = dates.filter(notEmpty).map((date) => dayjs(date))

  return dayjs.max(validDates)?.format(returnFormat)
}

/**
 * Convert a date to a relative time string (e.g. "3 days ago").
 *
 * @param DateToRelativeTimeParams
 */
export const dateToRelativeTime = ({
  date,
  hasSuffix = true,
}: {
  /* The date to convert */
  date: Optional<Date | string>
  /**
   * Whether to include a suffix like "ago"
   * @defaultValue `true`
   */
  hasSuffix?: boolean
}) => {
  if (!date) {
    return '-'
  }

  return dayjs(date).fromNow(!hasSuffix)
}

export const getDateXYearsAgo = (years: number, date: Optional<string>) => {
  if (!date) {
    return undefined
  }

  return dateAsServerStr(dayjs(date).subtract(years, 'year').toDate())
}

export const ageInYearsFormatter = (
  ageInYears: Optional<number>,
  translate: SourceReaderFunction<typeof en>,
): string => {
  if (isEmpty(ageInYears)) {
    return '-'
  }

  return ageInYears === 1
    ? `${translate('year', { years: ageInYears })}`
    : `${translate('years', { years: ageInYears })}`
}

type Period = {
  startDate: string
  endDate?: Optional<string>
}

/**
 * Given two periods, return true if they overlap. Granularity is at day level.
 */
export const doPeriodsOverlap = ([periodA, periodB]: [
  Period,
  Period,
]): boolean => {
  const maxStart = dayjs.max(dayjs(periodA.startDate), dayjs(periodB.startDate))
  const minEnd = dayjs.min(
    dayjs(periodA.endDate ?? dateUtils.MAX_DATE_STRING),
    dayjs(periodB.endDate ?? dateUtils.MAX_DATE_STRING),
  )

  return maxStart.isSame(minEnd, 'day') || maxStart.isBefore(minEnd, 'day')
}
